import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Globe, Building2, Trophy, TrendingDown } from 'lucide-react';
import { formatCurrency, formatPercentage } from './performanceUtils';

const BreakdownTable = ({ title, icon, description, data, nameKey, nameTransform }) => {
  if (!data || data.length === 0) return null;
  return (
    <Card className="portfolio-card">
      <CardHeader>
        <CardTitle className="text-xl font-serif flex items-center">
          {icon}
          {title}
        </CardTitle>
        <CardDescription className="body-text">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-medium text-slate-600">{nameKey}</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Invested</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Current Value</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">P&L</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Return %</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Count</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={nameTransform(item) || `item-${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium text-slate-900 capitalize"><span data-sensitive="identifier">{nameTransform(item)}</span></td>
                  <td className="py-3 px-4 text-right font-mono text-slate-700" data-sensitive="monetary">{formatCurrency(item.total_invested)}</td>
                  <td className="py-3 px-4 text-right font-mono text-slate-700" data-sensitive="monetary">{formatCurrency(item.current_value)}</td>
                  <td className={`py-3 px-4 text-right font-mono font-medium ${item.profit_loss >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-sensitive="monetary">
                    {formatCurrency(item.profit_loss)}
                  </td>
                  <td className={`py-3 px-4 text-right font-mono font-medium ${item.return_percentage >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-sensitive="rate">
                    {formatPercentage(item.return_percentage)}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-600">{item.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const PerformersList = ({ title, icon, description, performers, variant }) => {
  if (!performers || performers.length === 0) return null;
  const isPositive = variant === 'positive';
  const bgClass = isPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';
  const textClass = isPositive ? 'text-emerald-600' : 'text-red-600';

  return (
    <Card className="portfolio-card">
      <CardHeader>
        <CardTitle className="text-lg font-serif flex items-center">
          {icon}
          {title}
        </CardTitle>
        <CardDescription className="body-text">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {performers.map((item, index) => (
            <div key={item.name || `perf-${index}`} className={`flex items-center justify-between p-3 rounded-lg border ${bgClass}`}>
              <div className="flex-1">
                <p className="font-medium text-slate-900"><span data-sensitive="asset-name">{item.name}</span></p>
                <p className="text-xs text-slate-600 capitalize">{item.type.replace(/_/g, ' ')}</p>
              </div>
              <div className="text-right">
                <p className={`font-mono font-bold ${textClass}`} data-sensitive="monetary">
                  {isPositive ? '+' : ''}{formatCurrency(item.profit_loss)}
                </p>
                <p className={`text-xs ${textClass}`} data-sensitive="rate">
                  {isPositive ? '+' : ''}{item.return_percentage.toFixed(2)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceBreakdowns = ({ metrics }) => {
  // Sort mode for top/bottom performers: 'return' (% return — fairer default) | 'pl' (absolute $ P/L)
  const [sortMode, setSortMode] = useState('return');

  if (!metrics) return null;

  // Pick the sorted lists based on the toggle. Backend now returns both;
  // we fall back to the legacy keys so the UI never breaks if the response
  // shape regresses for any reason.
  const topPerformers = sortMode === 'return'
    ? (metrics.top_performers_by_return || metrics.top_performers || [])
    : (metrics.top_performers_by_pl || metrics.top_performers || []);
  const bottomPerformers = sortMode === 'return'
    ? (metrics.bottom_performers_by_return || metrics.bottom_performers || [])
    : (metrics.bottom_performers_by_pl || metrics.bottom_performers || []);
  const hasPerformers = topPerformers.length > 0 || bottomPerformers.length > 0;

  return (
    <>
      <BreakdownTable
        title="Performance by Asset Class"
        icon={<PieChart className="w-5 h-5 mr-2 text-blue-600" />}
        description="Returns breakdown by investment type"
        data={metrics.performance_by_asset_class}
        nameKey="Asset Class"
        nameTransform={(item) => item.type.replace(/_/g, ' ')}
      />
      <BreakdownTable
        title="Performance by Country"
        icon={<Globe className="w-5 h-5 mr-2 text-green-600" />}
        description="Geographic distribution of returns"
        data={metrics.performance_by_country}
        nameKey="Country"
        nameTransform={(item) => item.country}
      />
      <BreakdownTable
        title="Performance by Ownership"
        icon={<Building2 className="w-5 h-5 mr-2 text-purple-600" />}
        description="Direct vs Indirect vs Related portfolio returns"
        data={metrics.performance_by_ownership}
        nameKey="Ownership Type"
        nameTransform={(item) => item.ownership_type}
      />

      {hasPerformers && (
        <div className="space-y-3">
          {/* Sort toggle — controls both top and bottom cards in unison. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="performers-sort-toggle">
            <span className="text-xs font-medium text-slate-600">Sort by:</span>
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 self-start" role="group" aria-label="Sort top and bottom performers by">
              <button
                type="button"
                onClick={() => setSortMode('return')}
                aria-pressed={sortMode === 'return'}
                data-testid="performers-sort-by-return"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition focus:outline-hidden focus:ring-2 focus:ring-emerald-500 ${
                  sortMode === 'return'
                    ? 'bg-white shadow-xs text-emerald-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Return %
              </button>
              <button
                type="button"
                onClick={() => setSortMode('pl')}
                aria-pressed={sortMode === 'pl'}
                data-testid="performers-sort-by-pl"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition focus:outline-hidden focus:ring-2 focus:ring-emerald-500 ${
                  sortMode === 'pl'
                    ? 'bg-white shadow-xs text-emerald-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Profit/Loss $
              </button>
            </div>
            <span className="text-xs text-slate-500 sm:ml-2">
              {sortMode === 'return'
                ? 'Ranked by % return — small wins on small holdings can rank high.'
                : 'Ranked by absolute $ profit or loss — biggest dollar movers first.'}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PerformersList
              title="Top 5 Performers"
              icon={<Trophy className="w-5 h-5 mr-2 text-emerald-600" />}
              description={sortMode === 'return' ? 'Highest % return' : 'Best dollar gains'}
              performers={topPerformers}
              variant="positive"
            />
            <PerformersList
              title="Bottom 5 Performers"
              icon={<TrendingDown className="w-5 h-5 mr-2 text-red-600" />}
              description={sortMode === 'return' ? 'Lowest % return' : 'Worst dollar losses'}
              performers={bottomPerformers}
              variant="negative"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PerformanceBreakdowns;
